import { computed, onScopeDispose, ref, watch, type ComputedRef, type InjectionKey, type Ref } from "vue"

// App-wide stack of currently open Drawers, in the order they were opened. Every Drawer registers
// itself here (see components/ui/drawer/Drawer.vue), which is what turns Drawers opened from inside
// other Drawers into an iOS-style card stack: each one below the top recedes a step (see
// DrawerContent.vue) and comes back forward once the ones above it close.
//
// Tracked globally rather than through the component tree on purpose: vaul's own nested mode
// (`DrawerRootNested`) only works when the child Drawer is a component-tree descendant of the parent,
// and several of ours aren't — e.g. UserProfileDialog is mounted once at the app root and opened from
// inside the chat Drawer.
const openStack = ref<number[]>([])
let nextId = 0

/** How many Drawers are open right now. 2+ means there's an actual stack to lay out. */
export const drawerStackSize = computed(() => openStack.value.length)

// Natural (unstretched) heights of the open bottom sheets, reported by DrawerContent.vue. Stacked
// bottom sheets all take the tallest of these, so the pile lines up: one height for every card, and
// every card tall enough for its own content.
const sheetHeights = ref<Record<number, number>>({})

/** Shared height for stacked bottom sheets, in px — the tallest open sheet's natural height. */
export const stackSheetHeight = computed(() => {
  let max = 0
  for (const id of openStack.value) max = Math.max(max, sheetHeights.value[id] ?? 0)
  return max
})

export function reportSheetHeight(id: number, height: number) {
  if (sheetHeights.value[id] === height) return
  sheetHeights.value = { ...sheetHeights.value, [id]: height }
}

function forgetSheetHeight(id: number) {
  if (!(id in sheetHeights.value)) return
  const next = { ...sheetHeights.value }
  delete next[id]
  sheetHeights.value = next
}

export interface DrawerStackEntry {
  id: number
  /** 0 = top of the stack (or not stacked at all), 1 = directly underneath it, and so on. */
  depth: ComputedRef<number>
  /** Whether the Drawer is open (false while its close animation is still playing). */
  isOpen: Readonly<Ref<boolean>>
}

export const DRAWER_STACK_KEY: InjectionKey<DrawerStackEntry> = Symbol("DrawerStack")

export function useDrawerStackEntry(isOpen: Ref<boolean>): DrawerStackEntry {
  const id = nextId++

  function remove() {
    if (openStack.value.includes(id)) openStack.value = openStack.value.filter((entry) => entry !== id)
  }

  watch(isOpen, (open) => {
    if (open) {
      if (!openStack.value.includes(id)) openStack.value = [...openStack.value, id]
    } else {
      remove()
    }
  }, { immediate: true })

  // A Drawer unmounted while open (its page navigated away, a `v-if` flipped) must not leave the ones
  // under it stuck receded.
  onScopeDispose(() => {
    remove()
    forgetSheetHeight(id)
  })

  const depth = computed(() => {
    const index = openStack.value.indexOf(id)
    return index === -1 ? 0 : openStack.value.length - 1 - index
  })

  return { id, depth, isOpen }
}
